import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { useAuth, isTeenAccount } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { sanitizeStoryContent } from '../utils/renderBio'
import { normalizeRichTextStyles } from '../utils/normalizeRichText'
import { resizeImage } from '../lib/resizeImage'
import { decodeEntities } from '../lib/decodeEntities'
import * as Sentry from '@sentry/react'
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import {
  SortableContext, useSortable, arrayMove,
  horizontalListSortingStrategy, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const RESIZE_FALLBACK_MSG = "We couldn't process that image. Try a different photo, or save it as JPEG and retry."

// ─── Constants ─────────────────────────────────────────────────────────────────

const COVER_GRADIENTS = [
  { label: 'Twilight', value: 'linear-gradient(135deg, #1E0A3C 0%, #3B0764 35%, #831843 100%)' },
  { label: 'Ocean',    value: 'linear-gradient(135deg, #0F4C75 0%, #1E0A3C 50%, #3B0764 100%)' },
  { label: 'Forest',   value: 'linear-gradient(135deg, #064E3B 0%, #0F4C75 50%, #1E0A3C 100%)' },
  { label: 'Cosmic',   value: 'linear-gradient(135deg, #3B0764 0%, #831843 50%, #1D4ED8 100%)' },
  { label: 'Ember',    value: 'linear-gradient(135deg, #831843 0%, #FF6B35 50%, #3B0764 100%)' },
  { label: 'Stellar',  value: 'linear-gradient(135deg, #1D4ED8 0%, #7C3AED 50%, #EC4899 100%)' },
]

const COMMON_FANDOMS = [
  'Original Fiction', 'My Hero Academia', 'Harry Potter', 'Naruto',
  'Attack on Titan', 'Jujutsu Kaisen', 'Spy x Family', 'Dragon Ball Z', 'BTS RPF', 'Stray Kids RPF',
  'Vampire Diaries', 'Supernatural', 'Marvel', 'Star Wars', 'Haikyuu',
  'One Piece', 'Demon Slayer', 'Other',
]

const ALL_GENRES = [
  'Fantasy', 'Romance', 'Sci-Fi', 'Anime', 'Mystery', 'Horror',
  'Adventure', 'Drama', 'Comedy', 'Thriller', 'Historical', 'Slice of Life', 'Other',
]

const RATINGS   = ['Teen', 'Mature']
const STATUSES  = ['Ongoing', 'Complete', 'Hiatus']


function countWords(text) {
  const plain = text.replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/gi, ' ')
  return plain.trim().split(/\s+/).filter(Boolean).length
}

function readingTime(words) {
  const mins = Math.ceil(words / 250)
  return mins < 1 ? '< 1 min' : `${mins} min read`
}

async function pickImage(file, { maxEdge }) {
  const blob = await resizeImage(file, { maxEdge })
  return blob instanceof File ? blob : new File([blob], file.name, { type: blob.type })
}

// ─── Rich text formatting ────────────────────────────────────────────────────────

function execFormat(cmd, value = null) {
  document.execCommand('styleWithCSS', false, false)
  document.execCommand(cmd, false, value)
}


// ─── Sortable chapter tab ────────────────────────────────────────────────────────

function SortableChapterTab({ ch, isActive, showDelete, dragDisabled, onActivate, onDelete }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: ch.id, disabled: dragDisabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 'auto',
  }

  // Drag handle is rendered in both enabled and disabled states so the UI
  // shape stays identical pre/post-publish. When disabled, dnd-kit
  // listeners/attributes are omitted and visual cues (muted color,
  // not-allowed cursor, aria-disabled) communicate the state.
  return (
    <div
      ref={setNodeRef}
      style={style}
      role="tab"
      aria-selected={isActive}
      className={`write-chapter-tab${isActive ? ' write-chapter-tab--active' : ''}${isDragging ? ' write-chapter-tab--dragging' : ''}`}
      onClick={onActivate}
    >
      <span
        className={`write-chapter-tab__drag-handle${dragDisabled ? ' write-chapter-tab__drag-handle--disabled' : ''}`}
        aria-label="Drag to reorder chapter"
        aria-disabled={dragDisabled || undefined}
        onClick={(e) => e.stopPropagation()}
        {...(dragDisabled ? {} : attributes)}
        {...(dragDisabled ? {} : listeners)}
      >
        ⋮⋮
      </span>
      <span className="write-chapter-tab__name">
        {ch.title}
        {ch.hasDraft && <span className="write-chapter-tab__draft-dot" />}
        {ch.saveError && <span className="write-chapter-tab__error-dot" role="status" aria-label={`Save failed: ${ch.saveError}`} title={ch.saveError} />}
      </span>
      {showDelete && (
        <button
          className="write-chapter-tab__del"
          title="Delete chapter"
          aria-label="Delete chapter"
          onClick={(e) => { e.stopPropagation(); onDelete() }}
        >
          ×
        </button>
      )}
    </div>
  )
}


// ─── Main component ─────────────────────────────────────────────────────────────

export default function WritePage() {
  const navigate = useNavigate()
  const { user, profile, getToken } = useAuth()
  const toast = useToast()
  const isTeen = isTeenAccount(profile)
  const { storyId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const isEdit = Boolean(storyId)
  const [isPublished, setIsPublished] = useState(false)

  const [story, setStory] = useState({
    title:       '',
    description: '',
    fandom:      '',
    customFandom: '',
    genres:      [],
    tags:        [],
    rating:      'Teen',
    status:      'Ongoing',
    coverIdx:    0,
    disableComments: false,
    sexuallyExplicit: false,
    tipsEnabled: true,
  })

  const [chapters, setChapters] = useState([
    { id: 1, dbId: null, title: 'Chapter 1', content: '' },
  ])
  const [activeIdx, setActiveIdx] = useState(0)
  const [mode, setMode]           = useState('write') // 'write' | 'preview'
  const [saveStatus, setSaveStatus] = useState('saved') // 'saved' | 'saving' | 'unsaved'
  const [tagInput, setTagInput]     = useState('')
  const [published, setPublished]   = useState(false)
  const [publishedId, setPublishedId] = useState(null)
  const [publishing, setPublishing] = useState(false)
  const [publishError, setPublishError] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [coverFile, setCoverFile]       = useState(null)
  const [coverPreview, setCoverPreview] = useState(null)
  const [existingCoverUrl, setExistingCoverUrl] = useState(null)
  const [coverAspect, setCoverAspect]   = useState(null)
  const [coverFullscreen, setCoverFullscreen] = useState(false)
  const coverInputRef = useRef(null)

  const [bannerFile, setBannerFile]       = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [existingBannerUrl, setExistingBannerUrl] = useState(null)
  const [useCoverAsBanner, setUseCoverAsBanner] = useState(false)
  const bannerInputRef = useRef(null)

  const chapterIdRef          = useRef(2)
  const storyRef              = useRef(story)
  const chaptersRef           = useRef(chapters)
  const textareaRef           = useRef(null)
  const lastSyncedIdx         = useRef(-1)
  const deletedChapterDbIds   = useRef([])
  // Re-render-safe mirror of `deletedChapterDbIds.current.length > 0`. The ref
  // alone can't gate reorder draggability (mutating a ref doesn't re-render);
  // this state flips the gate the instant a DB-backed chapter is flagged for
  // deletion, and clears when an explicit Save/Publish flushes the deletes.
  const [hasPendingDeletes, setHasPendingDeletes] = useState(false)
  const importInputRef        = useRef(null)
  const pdfInputRef           = useRef(null)
  // Tracks chapter client-ids (ch.id) the user has actually modified since load
  // or last successful save. Used to skip chapter PUTs in handleSaveDraft on
  // published stories, preventing phantom drafts on click-Edit-then-leave.
  const dirtyChapterIdsRef    = useRef(new Set())

  const [activeFormats, setActiveFormats] = useState({
    bold: false, italic: false, underline: false,
    h1: false, h2: false, h3: false, blockquote: false, ul: false,
  })

  const activeChapter = chapters[activeIdx]

  // Auto-open file picker if ?import=html or ?import=pdf
  useEffect(() => {
    const importType = searchParams.get('import')
    if (importType === 'html') {
      setTimeout(() => importInputRef.current?.click(), 300)
      setSearchParams({}, { replace: true })
    } else if (importType === 'pdf') {
      setTimeout(() => pdfInputRef.current?.click(), 300)
      setSearchParams({}, { replace: true })
    } else if (importType === 'true') {
      setTimeout(() => {
        document.querySelectorAll('.write-import-btn').forEach(el => el.classList.add('write-import-btn--highlight'))
        setTimeout(() => document.querySelectorAll('.write-import-btn').forEach(el => el.classList.remove('write-import-btn--highlight')), 2000)
      }, 300)
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft to server when user leaves the page
  const autoSaveTriggered = useRef(false)
  const savePromiseRef = useRef(null)
  const publishPromiseRef = useRef(null)
  // Serializes every chapter-reorder RPC for this story (drag-reorder, Save,
  // Publish) so two negative-swap renumbers can't race into the RPC's 40001
  // concurrent-change guard.
  const reorderPromiseRef = useRef(null)
  const createdStoryIdRef = useRef(null)
  const saveDraftRef = useRef(null)
  const saveStatusRef = useRef(saveStatus)

  useEffect(() => { saveStatusRef.current = saveStatus }, [saveStatus])

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'hidden' && !autoSaveTriggered.current
          && !savePromiseRef.current && !publishPromiseRef.current && saveDraftRef.current) {
        autoSaveTriggered.current = true
        saveDraftRef.current(true).finally(() => { autoSaveTriggered.current = false })
      }
    }
    function onBeforeUnload(e) {
      if (saveStatus !== 'saved') {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [saveStatus])

  async function handleBack() {
    let needsConfirm = false
    let confirmMsg = ''

    if (saveDraftRef.current) {
      saveStatusRef.current = 'saving'; setSaveStatus('saving')
      const result = await saveDraftRef.current(true)
      if (result && !result.allSaved) {
        needsConfirm = true
        confirmMsg = `${result.failedCount} chapter(s) failed to save.`
      }
    }

    if (!needsConfirm && saveStatusRef.current !== 'saved') {
      needsConfirm = true
      confirmMsg = 'You have unsaved changes.'
    }

    if (needsConfirm) {
      if (!window.confirm(`${confirmMsg} Leave anyway? Unsaved changes will be lost.`)) return
    }

    navigate(isEdit ? `/story/${storyId}` : '/')
  }

  // Sync editor HTML only when the active chapter tab changes
  useEffect(() => {
    if (textareaRef.current && lastSyncedIdx.current !== activeIdx) {
      let html = chapters[activeIdx]?.content || ''
      // Convert plain text to paragraphs
      if (html && !/<p[\s>]/i.test(html)) {
        html = html.replace(/<\/?div[^>]*>/gi, '\n').replace(/<br\s*\/?>/gi, '\n')
        html = html.split(/\n{2,}/).filter(Boolean).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
      }
      textareaRef.current.innerHTML = sanitizeStoryContent(html)
      lastSyncedIdx.current = activeIdx
    }
  })

  useEffect(() => { storyRef.current = story }, [story])
  useEffect(() => { chaptersRef.current = chapters }, [chapters])

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        execFormat('bold'); syncFormats()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
        e.preventDefault()
        execFormat('italic'); syncFormats()
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (saveDraftRef.current) saveDraftRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Load existing story data when in edit mode
  useEffect(() => {
    if (!storyId) return
    async function loadForEdit() {
      try {
        const token = await getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const [storyRes, chapsRes] = await Promise.all([
          axios.get(`/api/stories/${storyId}`, { headers }),
          axios.get(`/api/stories/${storyId}/chapters`, { headers }),
        ])
        const s = storyRes.data
        setStory({
          title:        s.title ?? '',
          description:  s.description ?? '',
          fandom:       COMMON_FANDOMS.includes(s.fandom) ? s.fandom : (s.fandom ? 'Other' : ''),
          customFandom: COMMON_FANDOMS.includes(s.fandom) ? '' : (s.fandom ?? ''),
          genres:       s.genre ?? [],
          tags:         s.tags  ?? [],
          rating:       s.rating  ?? 'Teen',
          status:       s.status  ?? 'Ongoing',
          coverIdx:     s.coverIdx ?? 0,
          disableComments: s.disableComments ?? false,
          sexuallyExplicit: s.sexuallyExplicit ?? false,
          tipsEnabled: s.tipsEnabled ?? true,
        })
        if (s.coverUrl) {
          setExistingCoverUrl(s.coverUrl)
          const img = new Image()
          img.onload = () => setCoverAspect(`${img.naturalWidth} / ${img.naturalHeight}`)
          img.src = s.coverUrl
        }
        if (s.bannerUrl) setExistingBannerUrl(s.bannerUrl)
        setIsPublished(s.published ?? false)
        const loaded = chapsRes.data.map((ch) => {
          let content = Array.isArray(ch.content) ? ch.content.join('\n') : (ch.content ?? '')
          // Convert plain text to HTML paragraphs so the editor preserves formatting
          if (content && !/<p[\s>]/i.test(content)) {
            // Strip any stray <div> wrappers from contentEditable, keep inner text
            content = content.replace(/<\/?div[^>]*>/gi, '\n')
            content = content.replace(/<br\s*\/?>/gi, '\n')
            content = content.split(/\n{2,}/).filter(Boolean).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
          }
          // Prefer draft content if available (owner editing published story)
          let draftContent = ch.draftContent ? (Array.isArray(ch.draftContent) ? ch.draftContent.join('\n') : ch.draftContent) : null
          if (draftContent && !/<p[\s>]/i.test(draftContent)) {
            draftContent = draftContent.replace(/<\/?div[^>]*>/gi, '\n').replace(/<br\s*\/?>/gi, '\n')
            draftContent = draftContent.split(/\n{2,}/).filter(Boolean).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
          }
          return {
            id: ch.id, dbId: ch.id,
            title: ch.draftTitle ?? ch.title ?? '',
            content: draftContent ?? content,
            hasDraft: ch.hasDraft ?? false,
            publishedTitle: ch.title ?? '',
            publishedContent: content,
          }
        })
        setChapters(loaded.length ? loaded : [{ id: 1, dbId: null, title: 'Chapter 1', content: '', hasDraft: false, publishedTitle: '', publishedContent: '' }])
        setActiveIdx(0)
        lastSyncedIdx.current = -1
      } catch (err) {
        console.error('Failed to load story for editing:', err)
        setPublishError(err.response?.data?.error ?? 'Failed to load story for editing. Please go back and try again.')
      }
    }
    loadForEdit()
  }, [storyId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Read current format state from the editor
  function syncFormats() {
    if (!textareaRef.current) return
    const block = document.queryCommandValue('formatBlock').toLowerCase()
    setActiveFormats({
      bold:       document.queryCommandState('bold'),
      italic:     document.queryCommandState('italic'),
      underline:  document.queryCommandState('underline'),
      h1:         block === 'h1',
      h2:         block === 'h2',
      h3:         block === 'h3',
      blockquote: block === 'blockquote',
      ul:         document.queryCommandState('insertUnorderedList'),
    })
  }

  // Track active formats on selection/cursor change
  useEffect(() => {
    function updateFormats() {
      if (!textareaRef.current) return
      const sel = window.getSelection()
      if (!sel || !textareaRef.current.contains(sel.anchorNode)) return
      syncFormats()
    }
    document.addEventListener('selectionchange', updateFormats)
    return () => document.removeEventListener('selectionchange', updateFormats)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function updateStory(key, value) {
    saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
    setStory((s) => ({ ...s, [key]: value }))
  }

  function updateContent(val) {
    saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
    const activeId = chapters[activeIdx]?.id
    if (activeId != null) dirtyChapterIdsRef.current.add(activeId)
    setChapters((chs) =>
      chs.map((ch, i) => (i === activeIdx ? { ...ch, content: val } : ch))
    )
  }

  function updateChapterTitle(val) {
    saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
    const activeId = chapters[activeIdx]?.id
    if (activeId != null) dirtyChapterIdsRef.current.add(activeId)
    setChapters((chs) =>
      chs.map((ch, i) => (i === activeIdx ? { ...ch, title: val } : ch))
    )
  }

  function addChapter() {
    saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
    const newCh = { id: chapterIdRef.current++, dbId: null, title: `Chapter ${chapters.length + 1}`, content: '' }
    setChapters((chs) => [...chs, newCh])
    setActiveIdx(chapters.length)
  }

  // Drag-and-drop chapter reorder. Drafts-only: backend rejects on
  // published stories. Locally optimistic; rolls back on server error.
  const sensors = useSensors(
    useSensor(PointerSensor,  { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,    { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Reorder fires the atomic reorder RPC over the on-screen chapter ids, so it
  // must run only when the on-screen set matches the DB. Block it while there
  // are pending structural changes: a flagged-but-unsaved delete
  // (hasPendingDeletes) or a not-yet-saved new chapter (falsy dbId). Both clear
  // only on an explicit Save, which is when the structural sync runs.
  const hasUnsavedCreate = chapters.some((c) => !c.dbId)
  const canReorder = !isPublished && Boolean(storyId)
    && chapters.length > 1
    && !hasUnsavedCreate
    && !hasPendingDeletes

  // Single funnel for the drafts-only reorder_chapters RPC. Awaits any in-flight
  // reorder first, then issues its own, so Save/Publish/drag never overlap into
  // the RPC's concurrent-change (40001) guard. The atomic negative-swap is the
  // ONLY writer of chapters.number.
  async function runReorder(targetStoryId, orderedChapterIds, headers) {
    if (reorderPromiseRef.current) { try { await reorderPromiseRef.current } catch (_) { /* prior reorder error is the caller's to surface */ } }
    const p = axios.patch(`/api/stories/${targetStoryId}/chapters/reorder`, { orderedChapterIds }, { headers })
    reorderPromiseRef.current = p
    try { await p } finally { if (reorderPromiseRef.current === p) reorderPromiseRef.current = null }
  }

  async function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIdx = chapters.findIndex((c) => c.id === active.id)
    const newIdx = chapters.findIndex((c) => c.id === over.id)
    if (oldIdx < 0 || newIdx < 0) return

    const prev      = chapters
    const next      = arrayMove(chapters, oldIdx, newIdx)
    const activeChId = chapters[activeIdx]?.id

    saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
    setChapters(next)
    if (activeChId != null) {
      const nextActive = next.findIndex((c) => c.id === activeChId)
      if (nextActive >= 0) setActiveIdx(nextActive)
    }

    const orderedChapterIds = next.map((c) => c.dbId).filter(Boolean)
    if (orderedChapterIds.length !== next.length) return // safety: should be guarded by canReorder

    try {
      // Let any in-flight Save (which may create/delete/reorder) settle first so
      // this reorder operates on the reconciled id set.
      if (savePromiseRef.current) { try { await savePromiseRef.current } catch (_) { /* save surfaces its own errors */ } }
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      await runReorder(storyId, orderedChapterIds, headers)
    } catch (err) {
      setChapters(prev)
      if (activeChId != null) {
        const prevActive = prev.findIndex((c) => c.id === activeChId)
        if (prevActive >= 0) setActiveIdx(prevActive)
      }
      toast(err.response?.data?.error ?? 'Reorder failed. Please try again.', 'error')
    }
  }

  function deleteChapter(idx) {
    if (chapters.length === 1) return
    saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
    const ch = chapters[idx]
    if (ch.dbId) { deletedChapterDbIds.current.push(ch.dbId); setHasPendingDeletes(true) }
    setChapters((chs) => chs.filter((_, i) => i !== idx))
    setActiveIdx((i) => Math.min(i, chapters.length - 2))
  }

  function handleImportHtml(e) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    const reader = new FileReader()
    reader.onload = (ev) => {
      const html = ev.target.result
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      // Strip non-content elements
      doc.querySelectorAll('script, style, link, meta, nav, header, footer').forEach(el => el.remove())
      const bodyHtml = doc.body.innerHTML.trim()

      if (!bodyHtml) {
        toast('No content found in the HTML file.', 'error')
        return
      }

      // Try to extract title from the document
      const docTitle = doc.querySelector('title')?.textContent?.trim()
      if (docTitle && !story.title) updateStory('title', docTitle)

      // Try to split into chapters by "Chapter X" headings in the raw HTML
      const chapterRegex = /<h[1-4][^>]*>\s*(Chapter\s+\d+[^<]*)<\/h[1-4]>/gi
      const matches = [...bodyHtml.matchAll(chapterRegex)]

      if (matches.length >= 2) {
        const newChapters = []
        for (let i = 0; i < matches.length; i++) {
          const title = matches[i][1].replace(/<[^>]*>/g, '').trim()
          const startAfter = matches[i].index + matches[i][0].length
          const endAt = matches[i + 1]?.index ?? bodyHtml.length
          const content = bodyHtml.slice(startAfter, endAt).trim()
          if (content) {
            newChapters.push({
              id: chapterIdRef.current++,
              dbId: null,
              title: title || `Chapter ${i + 1}`,
              content: sanitizeStoryContent(content),
            })
          }
        }
        if (newChapters.length >= 2) {
          saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
          setChapters(newChapters)
          setActiveIdx(0)
          lastSyncedIdx.current = -1
          return
        }
      }

      // Fallback: load all content into the current chapter
      if (textareaRef.current) {
        textareaRef.current.innerHTML = sanitizeStoryContent(bodyHtml)
        updateContent(textareaRef.current.innerHTML)
        lastSyncedIdx.current = activeIdx
      }
    }
    reader.readAsText(file)
  }

  async function handleImportPdf(e) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    try {
      // Lazy-load pdfjs-dist (and configure its worker) only when a PDF is
      // actually imported, so it stays out of the eager main chunk (F2).
      const pdfjsLib = await import('pdfjs-dist')
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      const lines = []

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const textContent = await page.getTextContent()
        let lastY = null
        let line = ''

        for (const item of textContent.items) {
          if (lastY !== null && Math.abs(item.transform[5] - lastY) > 2) {
            lines.push(line)
            line = ''
          }
          line += item.str
          lastY = item.transform[5]
        }
        if (line) lines.push(line)
      }

      if (!lines.some(l => l.trim())) {
        toast('No text found in the PDF file.', 'error')
        return
      }

      // Try to split by "Chapter X" headings
      const chapterPattern = /^chapter\s+\d+/i
      const chapterStarts = []
      lines.forEach((line, idx) => {
        if (chapterPattern.test(line.trim())) chapterStarts.push(idx)
      })

      if (chapterStarts.length >= 2) {
        const newChapters = chapterStarts.map((startIdx, i) => {
          const title = lines[startIdx].trim()
          const endIdx = chapterStarts[i + 1] ?? lines.length
          const content = lines.slice(startIdx + 1, endIdx)
            .filter(l => l.trim())
            .map(l => `<p>${l.trim()}</p>`).join('')
          return {
            id: chapterIdRef.current++,
            dbId: null,
            title,
            content: sanitizeStoryContent(content),
          }
        }).filter(ch => ch.content)

        if (newChapters.length >= 2) {
          saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
          setChapters(newChapters)
          setActiveIdx(0)
          lastSyncedIdx.current = -1
          return
        }
      }

      // Fallback: load all into current chapter
      const html = lines.filter(l => l.trim()).map(l => `<p>${l.trim()}</p>`).join('')
      if (textareaRef.current) {
        textareaRef.current.innerHTML = sanitizeStoryContent(html)
        updateContent(textareaRef.current.innerHTML)
        lastSyncedIdx.current = activeIdx
      }
    } catch (err) {
      console.error('PDF import error:', err)
      toast('Failed to import PDF. Make sure the file contains readable text.', 'error')
    }
  }

  function toggleGenre(g) {
    updateStory('genres',
      story.genres.includes(g)
        ? story.genres.filter((x) => x !== g)
        : [...story.genres, g]
    )
  }

  function addTag(raw) {
    const tag = raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, '')
    if (tag && !story.tags.includes(tag) && story.tags.length < 10) {
      updateStory('tags', [...story.tags, tag])
    }
    setTagInput('')
  }

  function handleTagKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(tagInput)
    } else if (e.key === 'Backspace' && !tagInput) {
      updateStory('tags', story.tags.slice(0, -1))
    }
  }

  async function handleDeleteStory() {
    if (!window.confirm('Delete this story? This cannot be undone.')) return
    setDeleting(true)
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      await axios.delete(`/api/stories/${storyId}`, { headers })
      navigate('/')
    } catch (err) {
      console.error('Delete error:', err)
      toast(err.response?.data?.error ?? 'Failed to delete story.', 'error')
      setDeleting(false)
    }
  }

  async function handleDiscard() {
    if (!window.confirm('Discard this draft? Your changes will be lost.')) return
    setDeleting(true)
    try {
      // If autosave already created a DB row for this draft, soft-delete it.
      const orphanId = createdStoryIdRef.current
      if (orphanId) {
        const token = await getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        await axios.delete(`/api/stories/${orphanId}`, { headers })
      }
      navigate('/')
    } catch (err) {
      console.error('Discard error:', err)
      toast(err.response?.data?.error ?? 'Failed to discard draft.', 'error')
      setDeleting(false)
    }
  }

  async function handleSaveDraft(silent = false) {
    if (savePromiseRef.current) return savePromiseRef.current

    const hasContent = story.title.trim() || chapters.some(ch => ch.content.replace(/<[^>]*>/g, '').trim())
    if (!hasContent) return { allSaved: true, failedCount: 0 }

    if (!silent) setPublishing(true)
    saveStatusRef.current = 'saving'; setSaveStatus('saving')

    const promise = (async () => {
      let failedCount = 0
      let abortMsg = null
      const succeeded = new Set()

      // A chapter needs saving this pass if it's new (needs create) or, for an
      // existing row, the story is a draft or the chapter was edited. Mirrors the
      // dirty-skip that keeps published stories from gaining phantom drafts.
      const needsSave = (ch) => !ch.dbId || !isPublished || dirtyChapterIdsRef.current.has(ch.id)

      function recordFailure(err, ch, i, operation, targetId) {
        failedCount++
        const errMsg = !err.response ? 'Network error — please check your connection'
          : err.response.data?.error || err.message || 'Save failed'
        setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: errMsg } : c))
        try { Sentry.captureException(err, { tags: { surface: 'writepage_save' }, extra: { chapterIndex: i, chapterId: ch.id, chapterDbId: ch.dbId, operation, storyId: targetId, silent } }) } catch (_) { /* Sentry must not crash save */ }
        const status = err.response?.status
        if (!err.response || status === 401 || status === 403) {
          abortMsg = abortMsg ?? (!err.response ? 'Network error — please check your connection' : 'Authentication expired')
        }
      }

      try {
        const token = await getToken()
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const fandom = story.fandom === 'Other' ? story.customFandom || 'Other' : story.fandom || 'Original Fiction'
        const totalWords = chapters.reduce((s, ch) => s + countWords(ch.content), 0)

        const existingId = storyId || createdStoryIdRef.current
        if (existingId) {
          await axios.put(`/api/stories/${existingId}`, {
            title: story.title, description: story.description, fandom,
            genre: story.genres, tags: story.tags, rating: story.rating,
            status: story.status, cover_idx: story.coverIdx,
            disable_comments: story.disableComments,
            sexually_explicit: story.sexuallyExplicit,
            tips_enabled: story.tipsEnabled,
            chapters: chapters.length, words: totalWords,
          }, { headers })

          // Server owns chapter `number`. Flow: CREATE new -> DELETE flagged ->
          // REORDER (atomic RPC, the only writer of `number`) -> PUT content (no
          // number). Autosave (silent, fork B) does CREATE + content only and
          // DEFERS delete + reorder to an explicit Save.
          const dbIdByClientId = new Map(chapters.map(ch => [ch.id, ch.dbId ?? null]))

          // PHASE 1 — CREATE new chapters (server assigns MAX+1). Capture dbId so a
          // later save/autosave never re-POSTs the same chapter as a duplicate.
          for (let i = 0; i < chapters.length && !abortMsg; i++) {
            const ch = chapters[i]
            if (ch.dbId) continue
            try {
              const { data } = await axios.post(`/api/stories/${existingId}/chapters`,
                { title: ch.title, content: ch.content, words: countWords(ch.content) }, { headers })
              dbIdByClientId.set(ch.id, data.id)
              succeeded.add(ch.id)
              setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, dbId: data.id, saveError: undefined } : c))
            } catch (err) {
              recordFailure(err, ch, i, 'POST', existingId)
            }
          }

          // PHASE 2 — DELETE flagged rows BEFORE reorder so no phantom row holds a
          // survivor's slot. Explicit Save only; autosave defers structural deletes.
          if (!silent && !abortMsg && deletedChapterDbIds.current.length > 0) {
            const remaining = []
            for (const delId of deletedChapterDbIds.current) {
              try {
                await axios.delete(`/api/stories/${existingId}/chapters/${delId}`, { headers })
              } catch (err) {
                failedCount++
                remaining.push(delId)  // keep so a retry re-attempts it
                const status = err.response?.status
                if (!err.response || status === 401 || status === 403) {
                  abortMsg = abortMsg ?? (!err.response ? 'Network error — please check your connection' : 'Authentication expired')
                }
              }
            }
            deletedChapterDbIds.current = remaining
            setHasPendingDeletes(remaining.length > 0)
          }

          // PHASE 3 — REORDER via the atomic negative-swap RPC: the ONLY writer of
          // `number`. Drafts-only (RPC rejects published) and a no-op for <2.
          if (!silent && !abortMsg && !isPublished && chapters.length >= 2) {
            const orderedChapterIds = chapters.map(ch => dbIdByClientId.get(ch.id)).filter(Boolean)
            if (orderedChapterIds.length === chapters.length) {
              try {
                await runReorder(existingId, orderedChapterIds, headers)
              } catch (err) {
                failedCount++
              }
            }
          }

          // PHASE 4 — PUT content (no number) for pre-existing chapters. New chapters
          // already had their content persisted by their POST in PHASE 1.
          for (let i = 0; i < chapters.length && !abortMsg; i++) {
            const ch = chapters[i]
            if (!ch.dbId) continue
            if (isPublished && !dirtyChapterIdsRef.current.has(ch.id)) continue
            try {
              await axios.put(`/api/stories/${existingId}/chapters/${ch.dbId}`,
                { title: ch.title, content: ch.content, words: countWords(ch.content) }, { headers })
              dirtyChapterIdsRef.current.delete(ch.id)
              succeeded.add(ch.id)
              setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: undefined } : c))
            } catch (err) {
              recordFailure(err, ch, i, 'PUT', existingId)
            }
          }
        } else {
          const { data: newStory } = await axios.post('/api/stories', {
            title: story.title, description: story.description, fandom,
            genre: story.genres, tags: story.tags, rating: story.rating,
            status: story.status, cover_idx: story.coverIdx,
            disable_comments: story.disableComments,
            sexually_explicit: story.sexuallyExplicit,
            tips_enabled: story.tipsEnabled,
            published: false,
            chapters: chapters.length, words: totalWords,
          }, { headers })

          createdStoryIdRef.current = newStory.id

          // Brand-new story: POST chapters in array order (no number) -> server
          // MAX+1 yields 1..N contiguous. No reorder needed.
          for (let i = 0; i < chapters.length && !abortMsg; i++) {
            const ch = chapters[i]
            try {
              const { data } = await axios.post(`/api/stories/${newStory.id}/chapters`,
                { title: ch.title, content: ch.content, words: countWords(ch.content) }, { headers })
              succeeded.add(ch.id)
              setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, dbId: data.id, saveError: undefined } : c))
            } catch (err) {
              recordFailure(err, ch, i, 'POST', newStory.id)
            }
          }

          window.history.replaceState(null, '', `/story/${newStory.id}/edit`)
        }

        // On auth/network abort, flag every still-unsaved chapter that needed saving.
        if (abortMsg) {
          setChapters(prev => prev.map(c =>
            (!succeeded.has(c.id) && needsSave(c)) ? { ...c, saveError: c.saveError ?? abortMsg } : c
          ))
        }

        const targetId = storyId || createdStoryIdRef.current
        if (targetId) {
          if (coverFile) {
            const form = new FormData()
            form.append('file', coverFile)
            await axios.post(`/api/stories/${targetId}/cover`, form, { headers })
          }
          if (bannerFile) {
            const form = new FormData()
            form.append('file', bannerFile)
            await axios.post(`/api/stories/${targetId}/banner`, form, { headers })
          }
        }
      } catch (err) {
        if (!silent) setPublishError(err.response?.data?.error ?? 'Failed to save draft.')
        failedCount = chapters.length
      }
      return { allSaved: failedCount === 0, failedCount }
    })()

    savePromiseRef.current = promise
    let result
    try {
      result = await promise
    } finally {
      savePromiseRef.current = null
      if (!silent) setPublishing(false)
    }

    if (result.allSaved) {
      saveStatusRef.current = 'saved'; setSaveStatus('saved')
      if (!silent) toast('Draft saved!', 'success')
    } else {
      saveStatusRef.current = 'unsaved'; setSaveStatus('unsaved')
      toast('Some chapters could not be saved. Check chapter tabs for details.', 'error')
    }
    return result
  }

  saveDraftRef.current = handleSaveDraft

  async function handlePublishChapter() {
    const ch = chapters[activeIdx]
    if (!ch?.dbId) return
    const existingId = storyId || createdStoryIdRef.current
    if (!existingId) return
    setPublishing(true)
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      await axios.put(`/api/stories/${existingId}/chapters/${ch.dbId}`, {
        title: ch.title, content: ch.content, publish: true,
      }, { headers })
      // Clear dirty before the setChapters revert so the narrow window between
      // server-ack and client state update can't leave a stale dirty flag.
      dirtyChapterIdsRef.current.delete(ch.id)
      setChapters(prev => prev.map((c, i) => i === activeIdx
        ? { ...c, hasDraft: false, publishedTitle: c.title, publishedContent: c.content }
        : c
      ))
      saveStatusRef.current = 'saved'; setSaveStatus('saved')
      toast('Chapter published!', 'success')
    } catch (err) {
      setPublishError(err.response?.data?.error ?? 'Failed to publish chapter.')
    }
    setPublishing(false)
  }

  async function handleDiscardDraft() {
    const ch = chapters[activeIdx]
    if (!ch?.dbId) return
    const existingId = storyId || createdStoryIdRef.current
    if (!existingId) return
    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      await axios.delete(`/api/stories/${existingId}/chapters/${ch.dbId}/draft`, { headers })
      // Clear dirty before the setChapters revert (see handlePublishChapter).
      dirtyChapterIdsRef.current.delete(ch.id)
      setChapters(prev => prev.map((c, i) => i === activeIdx
        ? { ...c, title: c.publishedTitle, content: c.publishedContent, hasDraft: false }
        : c
      ))
      if (textareaRef.current) textareaRef.current.innerHTML = sanitizeStoryContent(ch.publishedContent)
      toast('Draft discarded', 'success')
    } catch (err) {
      setPublishError(err.response?.data?.error ?? 'Failed to discard draft.')
    }
  }

  async function handlePublish() {
    if (!story.title.trim()) {
      toast('Please add a title before publishing.', 'error')
      return
    }

    // Warn if content looks mature but is rated Teen
    if (story.rating === 'Teen') {
      const allText = (story.title + ' ' + story.description + ' ' + chapters.map(c => c.content).join(' ')).toLowerCase()
      const matureKeywords = ['sex', 'sexual', 'nsfw', 'explicit', 'smut', 'gore', 'torture', 'rape', 'suicide', 'self-harm', 'blood', 'murder', 'nude', 'nudity', 'erotic', 'orgasm', 'genital', 'intercourse', 'pornograph']
      const found = matureKeywords.filter(kw => allText.includes(kw))
      if (found.length > 0) {
        const proceed = window.confirm(
          'Warning: This story is rated Teen but may contain mature content.\n\n' +
          'If your story has explicit, sexual, or violent themes, please change the rating to Mature before publishing.\n\n' +
          'Misrated stories may be removed.\n\n' +
          'Press OK to publish as Teen, or Cancel to go back and change the rating.'
        )
        if (!proceed) return
      }
    }

    setPublishing(true)
    publishPromiseRef.current = true
    try {
      // Let any in-flight Save finish first so chapter dbIds are settled and the
      // two flows never issue overlapping reorder RPCs (C1 guard).
      if (savePromiseRef.current) { try { await savePromiseRef.current } catch (_) { /* save surfaces its own errors */ } }
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const fandom = story.fandom === 'Other' ? story.customFandom || 'Other' : story.fandom || 'Original Fiction'
      const totalWords = chapters.reduce((s, ch) => s + countWords(ch.content), 0)
      let failedCount = 0

      if (isEdit) {
        if (coverFile) {
          const form = new FormData()
          form.append('file', coverFile)
          await axios.post(`/api/stories/${storyId}/cover`, form, { headers })
        }
        if (bannerFile) {
          const form = new FormData()
          form.append('file', bannerFile)
          await axios.post(`/api/stories/${storyId}/banner`, form, { headers })
        }

        // Server owns chapter `number`. Run the structural sync while the story is
        // still a draft (CREATE -> DELETE -> REORDER -> PUT content), then flip
        // published:true LAST. The reorder RPC is drafts-only, so flipping first
        // would make it reject. An already-published story being re-saved keeps
        // isPublished true and skips reorder (middle-delete gap is out of scope).
        const dbIdByClientId = new Map(chapters.map(ch => [ch.id, ch.dbId ?? null]))
        let abortMsg = null

        // CREATE new chapters (server MAX+1); capture dbId before reorder.
        for (let i = 0; i < chapters.length && !abortMsg; i++) {
          const ch = chapters[i]
          if (ch.dbId) continue
          try {
            const { data } = await axios.post(`/api/stories/${storyId}/chapters`,
              { title: ch.title, content: ch.content, words: countWords(ch.content) }, { headers })
            dbIdByClientId.set(ch.id, data.id)
            setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, dbId: data.id, saveError: undefined } : c))
          } catch (err) {
            failedCount++
            const errMsg = !err.response ? 'Network error — please check your connection'
              : err.response.data?.error || err.message || 'Publish failed'
            setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: errMsg } : c))
            try { Sentry.captureException(err, { tags: { surface: 'writepage_publish' }, extra: { chapterIndex: i, chapterId: ch.id, chapterDbId: ch.dbId, operation: 'POST', storyId } }) } catch (_) { /* Sentry must not crash publish */ }
            const status = err.response?.status
            if (!err.response || status === 401 || status === 403) abortMsg = !err.response ? 'Network error — please check your connection' : 'Authentication expired'
          }
        }

        // DELETE flagged rows before reorder (no phantom row holds a slot).
        if (!abortMsg && deletedChapterDbIds.current.length > 0) {
          await Promise.all(deletedChapterDbIds.current.map(dbId =>
            axios.delete(`/api/stories/${storyId}/chapters/${dbId}`, { headers })
          ))
          deletedChapterDbIds.current = []
          setHasPendingDeletes(false)
        }

        // REORDER via the atomic RPC (drafts-only; >=2 chapters). Only writer of number.
        if (!abortMsg && !isPublished && chapters.length >= 2) {
          const orderedChapterIds = chapters.map(ch => dbIdByClientId.get(ch.id)).filter(Boolean)
          if (orderedChapterIds.length === chapters.length) {
            await runReorder(storyId, orderedChapterIds, headers)
          }
        }

        // PUT content (no number); publish:true clears any per-chapter draft.
        for (let i = 0; i < chapters.length && !abortMsg; i++) {
          const ch = chapters[i]
          if (!ch.dbId) continue
          try {
            await axios.put(`/api/stories/${storyId}/chapters/${ch.dbId}`,
              { title: ch.title, content: ch.content, words: countWords(ch.content), publish: true }, { headers })
            setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: undefined } : c))
          } catch (err) {
            failedCount++
            const errMsg = !err.response ? 'Network error — please check your connection'
              : err.response.data?.error || err.message || 'Publish failed'
            setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: errMsg } : c))
            try { Sentry.captureException(err, { tags: { surface: 'writepage_publish' }, extra: { chapterIndex: i, chapterId: ch.id, chapterDbId: ch.dbId, operation: 'PUT', storyId } }) } catch (_) { /* Sentry must not crash publish */ }
            const status = err.response?.status
            if (!err.response || status === 401 || status === 403) abortMsg = !err.response ? 'Network error — please check your connection' : 'Authentication expired'
          }
        }

        // Flip published:true LAST, together with the metadata update.
        if (!abortMsg) {
          await axios.put(`/api/stories/${storyId}`, {
            title:       story.title,
            description: story.description,
            fandom,
            genre:       story.genres,
            tags:        story.tags,
            rating:      story.rating,
            status:      story.status,
            cover_idx:   story.coverIdx,
            disable_comments: story.disableComments,
            sexually_explicit: story.sexuallyExplicit,
            tips_enabled: story.tipsEnabled,
            published:   true,
            chapters:    chapters.length,
            words:       totalWords,
          }, { headers })
        }

        if (failedCount === 0) {
          setChapters(prev => prev.map(c => ({ ...c, hasDraft: false, publishedTitle: c.title, publishedContent: c.content })))
          dirtyChapterIdsRef.current.clear()
          setPublishedId(storyId)
          setPublished(true)
        } else {
          setPublishError(`${failedCount} chapter(s) failed to publish. Check chapter tabs for details.`)
        }
      } else {
        const { data: newStory } = await axios.post('/api/stories', {
          title:       story.title,
          description: story.description,
          fandom,
          genre:       story.genres,
          tags:        story.tags,
          rating:      story.rating,
          status:      story.status,
          cover_idx:   story.coverIdx,
          disable_comments: story.disableComments,
          sexually_explicit: story.sexuallyExplicit,
          tips_enabled: story.tipsEnabled,
          published:   true,
          chapters:    chapters.length,
          words:       totalWords,
        }, { headers })

        if (coverFile) {
          const form = new FormData()
          form.append('file', coverFile)
          await axios.post(`/api/stories/${newStory.id}/cover`, form, { headers })
        }
        if (bannerFile) {
          const form = new FormData()
          form.append('file', bannerFile)
          await axios.post(`/api/stories/${newStory.id}/banner`, form, { headers })
        }

        for (let i = 0; i < chapters.length; i++) {
          const ch = chapters[i]
          try {
            await axios.post(`/api/stories/${newStory.id}/chapters`, {
              title:   ch.title,
              content: ch.content,
              words:   countWords(ch.content),
              published_at: new Date().toISOString(),
            }, { headers })
            setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: undefined } : c))
          } catch (err) {
            failedCount++
            const errMsg = !err.response ? 'Network error — please check your connection'
              : err.response.data?.error || err.message || 'Publish failed'
            setChapters(prev => prev.map(c => c.id === ch.id ? { ...c, saveError: errMsg } : c))
            try { Sentry.captureException(err, { tags: { surface: 'writepage_publish' }, extra: { chapterIndex: i, chapterId: ch.id, operation: 'POST', storyId: newStory.id } }) } catch (_) { /* Sentry must not crash publish */ }
            const status = err.response?.status
            if (!err.response || status === 401 || status === 403) {
              const remainMsg = !err.response ? 'Network error — please check your connection' : 'Authentication expired'
              for (let k = i + 1; k < chapters.length; k++) {
                failedCount++
                const ck = chapters[k]
                setChapters(prev => prev.map(c => c.id === ck.id ? { ...c, saveError: remainMsg } : c))
              }
              break
            }
          }
        }

        if (failedCount === 0) {
          setPublishedId(newStory.id)
          setPublished(true)
          dirtyChapterIdsRef.current.clear()
        } else {
          setPublishError(`${failedCount} chapter(s) failed to publish. Check chapter tabs for details.`)
        }
      }
    } catch (err) {
      console.error('Publish error:', err)
      setPublishError(err.response?.data?.error ?? (isEdit ? 'Failed to save. Please try again.' : 'Failed to publish. Please try again.'))
    } finally {
      publishPromiseRef.current = null
      setPublishing(false)
    }
  }

  const words   = countWords(activeChapter.content)
  const allWords = chapters.reduce((s, ch) => s + countWords(ch.content), 0)

  if (published) {
    return <PublishSuccess story={story} chapters={chapters} storyId={publishedId} onEdit={() => setPublished(false)} navigate={navigate} isEdit={isEdit} />
  }

  return (
    <div className="write-page">

      {/* ── Top bar ── */}
      <div className="write-topbar">
        <div className="write-topbar__left">
          <button type="button" onClick={handleBack} className="write-topbar__back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {isEdit ? 'Back to story' : 'Verse'}
          </button>
          <div className="write-topbar__divider" />
          <input
            className="write-topbar__title"
            placeholder="Untitled story…"
            value={story.title}
            onChange={(e) => updateStory('title', e.target.value)}
          />
        </div>

        <div className="write-topbar__right">
          <SaveIndicator status={saveStatus} />
          <button
            className={`btn write-topbar__mode-btn${mode === 'preview' ? ' write-topbar__mode-btn--active' : ''}`}
            onClick={() => setMode((m) => (m === 'write' ? 'preview' : 'write'))}
          >
            {mode === 'write' ? '👁 Preview' : '✏️ Edit'}
          </button>
          {publishError && (
            <span style={{ color: '#ef4444', fontSize: '0.82rem', maxWidth: 220, textAlign: 'right' }}>
              {publishError}
            </span>
          )}
          {isEdit && (
            <button
              className="btn write-topbar__delete"
              onClick={handleDeleteStory}
              disabled={deleting || publishing}
              title="Delete story"
            >
              {deleting ? 'Deleting…' : 'Delete story'}
            </button>
          )}
          {!isEdit && (
            <button
              className="btn write-topbar__delete"
              onClick={handleDiscard}
              disabled={deleting || publishing}
              title="Discard draft"
            >
              {deleting ? 'Discarding…' : 'Discard'}
            </button>
          )}
          <button className="btn write-topbar__draft" onClick={() => { setPublishError(''); handleSaveDraft() }} disabled={publishing}>
            {publishing ? 'Saving…' : 'Save Draft'}
          </button>
          <button className="btn write-topbar__publish" onClick={() => { setPublishError(''); handlePublish() }} disabled={publishing}>
            {publishing ? (isEdit && isPublished ? 'Saving…' : 'Publishing…') : (isEdit && isPublished ? 'Save changes' : 'Publish')}
          </button>
          {isEdit && isPublished && chapters[activeIdx]?.hasDraft && (
            <>
              <button className="btn write-topbar__publish-chapter" onClick={() => { setPublishError(''); handlePublishChapter() }} disabled={publishing}>
                Publish chapter
              </button>
              <button className="btn write-topbar__discard" onClick={handleDiscardDraft} disabled={publishing}>
                Discard
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="write-body">

        {/* Mobile sidebar toggle */}
        <button className="write-sidebar-toggle" onClick={() => setSidebarOpen(v => !v)}>
          {sidebarOpen ? '✕ Close details' : '⚙ Story details'}
        </button>

        {/* Sidebar overlay for mobile */}
        {sidebarOpen && <div className="write-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* Sidebar */}
        <aside className={`write-sidebar${sidebarOpen ? ' write-sidebar--open' : ''}`}>
          <div className="write-sidebar__inner">

            {/* Cover */}
            <div className="write-sidebar__section">
              <div className="write-sidebar__label">Cover</div>

              {/* Preview — shows uploaded image or gradient; click to fullscreen */}
              <div
                className="write-cover-preview"
                style={{
                  background: (coverPreview || existingCoverUrl) ? undefined : COVER_GRADIENTS[story.coverIdx].value,
                  backgroundImage: (coverPreview || existingCoverUrl) ? `url(${coverPreview || existingCoverUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  aspectRatio: (coverPreview || existingCoverUrl) && coverAspect ? coverAspect : '3 / 2',
                  position: 'relative',
                  cursor: 'zoom-in',
                }}
                onClick={() => setCoverFullscreen(true)}
              >
                {!(coverPreview || existingCoverUrl) && (
                  <span className="write-cover-initial">
                    {story.title?.[0]?.toUpperCase() || '?'}
                  </span>
                )}
                {(coverPreview || existingCoverUrl) && (
                  <button
                    className="write-cover-remove"
                    title="Remove image"
                    aria-label="Remove cover image"
                    onClick={() => { setCoverFile(null); setCoverPreview(null); setExistingCoverUrl(null); setCoverAspect(null) }}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Upload button */}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  const input = e.target
                  if (!file) return
                  input.value = ''
                  const url = URL.createObjectURL(file)
                  const img = new Image()
                  img.onload = () => {
                    setCoverAspect(`${img.naturalWidth} / ${img.naturalHeight}`)
                  }
                  img.src = url
                  try {
                    const coverResized = await pickImage(file, { maxEdge: 1920 })
                    setCoverFile(coverResized)
                    setCoverPreview(url)
                    setExistingCoverUrl(null)
                    if (useCoverAsBanner) {
                      const bannerResized = await pickImage(file, { maxEdge: 1920 })
                      setBannerFile(bannerResized)
                      setBannerPreview(url)
                      setExistingBannerUrl(null)
                    }
                  } catch {
                    toast(RESIZE_FALLBACK_MSG, 'error')
                  }
                }}
              />
              <button
                className="write-cover-upload-btn"
                onClick={() => coverInputRef.current?.click()}
              >
                {(coverPreview || existingCoverUrl) ? '🖼 Change image' : '📁 Upload image'}
              </button>
              <div className="write-sidebar__hint">
                Recommended: 1600 × 2400 px (2:3 portrait) · JPEG, PNG or WebP · max 4 MB
              </div>
              <div className="write-sidebar__hint" style={{ color: 'var(--text-muted)' }}>
                Only use images you own or have permission to use. Using copyrighted images may result in removal.
              </div>

              {/* Gradient swatches — only relevant when no image */}
              {!(coverPreview || existingCoverUrl) && (
                <div className="write-cover-picker">
                  {COVER_GRADIENTS.map((g, i) => (
                    <button
                      key={i}
                      className={`write-cover-swatch${story.coverIdx === i ? ' write-cover-swatch--active' : ''}`}
                      style={{ background: g.value }}
                      title={g.label}
                      onClick={() => updateStory('coverIdx', i)}
                    />
                  ))}
                </div>
              )}

              <label className="write-sidebar__label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={useCoverAsBanner}
                  onChange={(e) => {
                    setUseCoverAsBanner(e.target.checked)
                    if (e.target.checked && (coverFile || existingCoverUrl)) {
                      setBannerFile(coverFile)
                      setBannerPreview(coverPreview || existingCoverUrl)
                      if (!coverFile && existingCoverUrl) setExistingBannerUrl(existingCoverUrl)
                    }
                  }}
                  disabled={!coverFile && !coverPreview && !existingCoverUrl}
                />
                Use cover as story card banner
              </label>
            </div>

            {/* Banner */}
            <div className="write-sidebar__section" style={{ opacity: useCoverAsBanner ? 0.4 : 1, pointerEvents: useCoverAsBanner ? 'none' : 'auto' }}>
              <div className="write-sidebar__label">Story Card Banner</div>

              {/* Banner preview */}
              {(bannerPreview || existingBannerUrl) ? (
                <div className="write-banner-preview" style={{ position: 'relative' }}>
                  <img
                    src={bannerPreview || existingBannerUrl}
                    alt="Banner preview"
                    className="write-banner-preview__img"
                  />
                  <button
                    className="write-cover-remove"
                    title="Remove banner"
                    aria-label="Remove banner image"
                    onClick={() => { setBannerFile(null); setBannerPreview(null); setExistingBannerUrl(null) }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="write-banner-preview write-banner-preview--empty">
                  <span className="write-sidebar__hint" style={{ margin: 0 }}>No banner — gradient used</span>
                </div>
              )}

              <input
                ref={bannerInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  const input = e.target
                  if (!file) return
                  input.value = ''
                  try {
                    const resized = await pickImage(file, { maxEdge: 1920 })
                    setBannerFile(resized)
                    setBannerPreview(URL.createObjectURL(file))
                    setExistingBannerUrl(null)
                  } catch {
                    toast(RESIZE_FALLBACK_MSG, 'error')
                  }
                }}
              />
              <button
                className="write-cover-upload-btn"
                onClick={() => bannerInputRef.current?.click()}
              >
                {(bannerPreview || existingBannerUrl) ? '🖼 Change banner' : '📁 Upload banner'}
              </button>
              <div className="write-sidebar__hint">
                Recommended: 1500 × 500 px (3:1 landscape) · JPEG, PNG or WebP · max 4 MB
              </div>
              <div className="write-sidebar__hint" style={{ color: 'var(--text-muted)' }}>
                Only use images you own or have permission to use. Using copyrighted images may result in removal.
              </div>
            </div>

            {/* Description */}
            <div className="write-sidebar__section">
              <label className="write-sidebar__label" htmlFor="ws-desc">Description</label>
              <textarea
                id="ws-desc"
                className="write-sidebar__textarea"
                placeholder="What's your story about? (Shown in story cards)"
                value={story.description}
                onChange={(e) => updateStory('description', e.target.value)}
                rows={4}
              />
            </div>

            {/* Fandom */}
            <div className="write-sidebar__section">
              <label className="write-sidebar__label" htmlFor="ws-fandom">Fandom</label>
              <select
                id="ws-fandom"
                className="write-sidebar__select"
                value={story.fandom}
                onChange={(e) => updateStory('fandom', e.target.value)}
              >
                <option value="">Select a fandom…</option>
                {COMMON_FANDOMS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              {story.fandom === 'Other' && (
                <input
                  className="write-sidebar__input"
                  aria-label="Custom fandom name"
                  placeholder="Enter fandom name…"
                  value={story.customFandom}
                  onChange={(e) => updateStory('customFandom', e.target.value)}
                  style={{ marginTop: 8 }}
                />
              )}
            </div>

            {/* Genres */}
            <div className="write-sidebar__section">
              <div className="write-sidebar__label">Genres <span className="write-sidebar__hint">(pick up to 3)</span></div>
              <div className="write-genre-grid">
                {ALL_GENRES.map((g) => (
                  <button
                    key={g}
                    className={`write-genre-chip${story.genres.includes(g) ? ' write-genre-chip--on' : ''}`}
                    onClick={() => toggleGenre(g)}
                    disabled={!story.genres.includes(g) && story.genres.length >= 3}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="write-sidebar__section">
              <div className="write-sidebar__label">
                Tags <span className="write-sidebar__hint">({story.tags.length}/10)</span>
              </div>
              <div className="write-tag-input-wrap">
                {story.tags.map((t) => (
                  <span key={t} className="write-tag">
                    #{t}
                    <button className="write-tag__remove" aria-label={`Remove tag ${t}`} onClick={() => updateStory('tags', story.tags.filter((x) => x !== t))}>×</button>
                  </span>
                ))}
                {story.tags.length < 10 && (
                  <input
                    className="write-tag-input"
                    aria-label="Add a tag"
                    placeholder="Add tag…"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKey}
                    onBlur={() => tagInput && addTag(tagInput)}
                  />
                )}
              </div>
              <div className="write-sidebar__hint" style={{ marginTop: 6 }}>Press Enter or comma to add</div>
            </div>

            {/* Rating & Status */}
            <div className="write-sidebar__section write-sidebar__row">
              <div style={{ flex: 1 }}>
                <label className="write-sidebar__label" htmlFor="ws-rating">Rating</label>
                <select
                  id="ws-rating"
                  className="write-sidebar__select"
                  value={story.rating}
                  onChange={(e) => {
                    updateStory('rating', e.target.value)
                    if (e.target.value !== 'Mature') updateStory('sexuallyExplicit', false)
                  }}
                >
                  {RATINGS.filter(r => !(isTeen && r === 'Mature')).map((r) => <option key={r}>{r}</option>)}
                </select>
                <p style={{ fontSize: '0.68rem', color: 'var(--text-faint)', margin: '4px 0 0', lineHeight: 1.4 }}>
                  {story.rating === 'Mature'
                    ? 'Mature: explicit violence, heavy themes, or sexual content beyond mild themes. Readers must confirm before viewing.'
                    : 'Teen: suitable for ages 13+. Romance, kissing, and mild sexual references are OK — no graphic or explicit content.'}
                </p>
              </div>
              <div style={{ flex: 1 }}>
                <label className="write-sidebar__label" htmlFor="ws-status">Status</label>
                <select
                  id="ws-status"
                  className="write-sidebar__select"
                  value={story.status}
                  onChange={(e) => updateStory('status', e.target.value)}
                >
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Sexually explicit checkbox — only shown for Mature stories */}
            {story.rating === 'Mature' && (
              <div className="write-sidebar__section">
                <label className="write-sidebar__label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={story.sexuallyExplicit}
                    onChange={(e) => updateStory('sexuallyExplicit', e.target.checked)}
                  />
                  Contains sexually explicit content
                </label>
                <p style={{ fontSize: '0.68rem', color: 'var(--text-faint)', margin: '4px 0 0', lineHeight: 1.4 }}>
                  <strong>Mature</strong> covers heavy violence, dark themes, and sexual tension — romance, implied intimacy, and fading to black are fine here. Tips remain enabled.<br />
                  <strong>Sexually explicit</strong> means your story contains graphic, detailed descriptions of sexual acts (not just implied or fade-to-black). Check this box if it does — tipping will be disabled on this story. Readers will see an "Explicit" label.
                </p>
              </div>
            )}

            {/* Comments toggle */}
            <div className="write-sidebar__section">
              <label className="write-sidebar__label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={story.disableComments}
                  onChange={(e) => updateStory('disableComments', e.target.checked)}
                />
                Disable comments
              </label>
            </div>

            {/* Tips toggle — hidden until payments are enabled */}
            {import.meta.env.VITE_TIPS_ENABLED === 'true' && (
              <div className="write-sidebar__section">
                <label className="write-sidebar__label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={story.tipsEnabled}
                    onChange={(e) => updateStory('tipsEnabled', e.target.checked)}
                  />
                  Allow tips on this story
                </label>
              </div>
            )}

            {/* Story-wide stats */}
            <div className="write-story-stats">
              <div className="write-story-stat">
                <span className="write-story-stat__value">{chapters.length}</span>
                <span className="write-story-stat__label">Chapters</span>
              </div>
              <div className="write-story-stat">
                <span className="write-story-stat__value">{allWords.toLocaleString()}</span>
                <span className="write-story-stat__label">Total words</span>
              </div>
            </div>

          </div>
        </aside>

        {/* ── Editor panel ── */}
        <div className="write-editor-panel">

          {/* Chapter tabs */}
          <div className="write-chapter-tabs">
            {!isPublished && chapters.length > 1 && (hasPendingDeletes || hasUnsavedCreate) && (
              <div className="write-reorder-callout" role="status">
                <span className="write-reorder-callout__icon" aria-hidden="true">↕</span>
                Reordering paused — save your changes to drag chapters.
              </div>
            )}
            <DndContext
              sensors={sensors}
              onDragEnd={handleDragEnd}
              accessibility={{
                announcements: {
                  onDragStart({ active }) {
                    const idx = chapters.findIndex((c) => c.id === active.id)
                    return `Picked up chapter at position ${idx + 1} of ${chapters.length}.`
                  },
                  onDragOver({ active, over }) {
                    if (!over) return
                    const newIdx = chapters.findIndex((c) => c.id === over.id)
                    return `Chapter moved to position ${newIdx + 1} of ${chapters.length}.`
                  },
                  onDragEnd({ active, over }) {
                    if (!over) return `Reorder cancelled.`
                    const newIdx = chapters.findIndex((c) => c.id === over.id)
                    return `Chapter dropped at position ${newIdx + 1} of ${chapters.length}.`
                  },
                  onDragCancel({ active }) {
                    const idx = chapters.findIndex((c) => c.id === active.id)
                    return `Reorder cancelled. Chapter returned to position ${idx + 1}.`
                  },
                },
              }}
            >
              <SortableContext
                items={chapters.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="write-chapter-tabs__list" role="tablist" aria-label="Chapters">
                  {chapters.map((ch, i) => (
                    <SortableChapterTab
                      key={ch.id}
                      ch={ch}
                      isActive={i === activeIdx}
                      showDelete={chapters.length > 1}
                      dragDisabled={!canReorder}
                      onActivate={() => setActiveIdx(i)}
                      onDelete={() => deleteChapter(i)}
                    />
                  ))}
                  <button className="write-chapter-add" onClick={addChapter} title="Add chapter">
                    + Chapter
                  </button>
                  <button className="write-chapter-add write-import-btn" onClick={() => importInputRef.current?.click()} title="Import from HTML file">
                    ↥ HTML
                  </button>
                  <button className="write-chapter-add write-import-btn" onClick={() => pdfInputRef.current?.click()} title="Import from PDF file">
                    ↥ PDF
                  </button>
                </div>
              </SortableContext>
            </DndContext>
            {isPublished && chapters.length > 1 && (
              <div className="write-chapter-tabs__published-notice">
                Chapter order is fixed once a story is published.
              </div>
            )}
          </div>

          {mode === 'write' ? (
            <>
              {/* Toolbar */}
              <div className="write-toolbar" role="toolbar" aria-label="Text formatting">
                <div className="write-toolbar__sep" />

                <button className={`write-toolbar__btn write-toolbar__btn--bold${activeFormats.bold ? ' write-toolbar__btn--active' : ''}`} title="Bold (Ctrl+B)" aria-label="Bold"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('bold'); syncFormats() }}>B</button>
                <button className={`write-toolbar__btn write-toolbar__btn--italic${activeFormats.italic ? ' write-toolbar__btn--active' : ''}`} title="Italic (Ctrl+I)" aria-label="Italic"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('italic'); syncFormats() }}>I</button>
                <button className={`write-toolbar__btn write-toolbar__btn--underline${activeFormats.underline ? ' write-toolbar__btn--active' : ''}`} title="Underline (Ctrl+U)" aria-label="Underline"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('underline'); syncFormats() }}>U</button>

                <div className="write-toolbar__sep" />

                <button className={`write-toolbar__btn${activeFormats.h1 ? ' write-toolbar__btn--active' : ''}`} title="Heading 1" aria-label="Heading 1"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('formatBlock', activeFormats.h1 ? 'P' : 'H1'); syncFormats() }}>H1</button>
                <button className={`write-toolbar__btn${activeFormats.h2 ? ' write-toolbar__btn--active' : ''}`} title="Heading 2" aria-label="Heading 2"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('formatBlock', activeFormats.h2 ? 'P' : 'H2'); syncFormats() }}>H2</button>
                <button className={`write-toolbar__btn${activeFormats.h3 ? ' write-toolbar__btn--active' : ''}`} title="Heading 3" aria-label="Heading 3"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('formatBlock', activeFormats.h3 ? 'P' : 'H3'); syncFormats() }}>H3</button>

                <div className="write-toolbar__sep" />

                <button className={`write-toolbar__btn${activeFormats.blockquote ? ' write-toolbar__btn--active' : ''}`} title="Blockquote" aria-label="Blockquote"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('formatBlock', activeFormats.blockquote ? 'P' : 'BLOCKQUOTE'); syncFormats() }}>❝</button>
                <button className={`write-toolbar__btn${activeFormats.ul ? ' write-toolbar__btn--active' : ''}`} title="Bullet list" aria-label="Bullet list"
                  onMouseDown={(e) => { e.preventDefault(); execFormat('insertUnorderedList'); syncFormats() }}>≡</button>
                <button className="write-toolbar__btn" title="Divider" aria-label="Insert divider"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    const sel = window.getSelection()
                    if (!sel.rangeCount) return
                    const range = sel.getRangeAt(0)
                    range.deleteContents()
                    const hr = document.createElement('hr')
                    const br = document.createElement('br')
                    range.insertNode(br)
                    range.insertNode(hr)
                    range.setStartAfter(br)
                    range.collapse(true)
                    sel.removeAllRanges()
                    sel.addRange(range)
                    if (textareaRef.current) updateContent(textareaRef.current.innerHTML)
                  }}>—</button>

                <input
                  ref={importInputRef}
                  type="file"
                  accept=".html,.htm"
                  style={{ display: 'none' }}
                  onChange={handleImportHtml}
                />
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={handleImportPdf}
                />

                <div className="write-toolbar__spacer" />
                <span className="write-toolbar__hint">Rich text</span>
              </div>

              {/* Draft banner */}
              {isEdit && isPublished && activeChapter?.hasDraft && (
                <div className="write-draft-banner">You have unpublished changes to this chapter</div>
              )}

              {/* Chapter title */}
              <div className="write-chapter-title-wrap">
                <input
                  className="write-chapter-title-input"
                  placeholder="Chapter title…"
                  value={activeChapter.title}
                  onChange={(e) => updateChapterTitle(e.target.value)}
                />
              </div>

              {/* Writing area */}
              <div
                ref={textareaRef}
                className="write-editor"
                contentEditable
                suppressContentEditableWarning
                spellCheck
                data-placeholder={`Start writing "${activeChapter.title}"…`}
                onInput={() => {
                  if (textareaRef.current) updateContent(textareaRef.current.innerHTML)
                }}
                onPaste={(e) => {
                  e.preventDefault()
                  const html = e.clipboardData.getData('text/html')
                  const text = e.clipboardData.getData('text/plain')
                  const clean = html
                    ? sanitizeStoryContent(normalizeRichTextStyles(html))
                    : text.split('\n').filter(l => l.trim()).map(l => `<p>${l}</p>`).join('')
                  document.execCommand('insertHTML', false, clean)
                }}
              />
            </>
          ) : (
            <PreviewPanel chapter={activeChapter} story={story} />
          )}

          {/* Status bar */}
          <div className="write-statusbar">
            <span>{words.toLocaleString()} words</span>
            <span className="write-statusbar__dot" />
            <span>{readingTime(words)}</span>
            <span className="write-statusbar__dot" />
            <span>{activeChapter.content.length.toLocaleString()} chars</span>
            {mode === 'write' && (
              <>
                <span className="write-statusbar__dot" />
                <span className="write-statusbar__shortcut">Ctrl+B bold · Ctrl+I italic · Ctrl+U underline · Ctrl+S save</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Fullscreen cover preview overlay ── */}
      {coverFullscreen && (
        <div
          className="write-cover-fullscreen"
          onClick={() => setCoverFullscreen(false)}
        >
          <div
            className="write-cover-fullscreen__inner"
            style={{
              background: (coverPreview || existingCoverUrl) ? undefined : COVER_GRADIENTS[story.coverIdx].value,
              backgroundImage: (coverPreview || existingCoverUrl) ? `url(${coverPreview || existingCoverUrl})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {!(coverPreview || existingCoverUrl) && (
              <span className="write-cover-fullscreen__initial">
                {story.title?.[0]?.toUpperCase() || '?'}
              </span>
            )}
          </div>
          <button className="write-cover-fullscreen__close" onClick={() => setCoverFullscreen(false)}>✕</button>
          <p className="write-cover-fullscreen__hint">Click anywhere to close</p>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function SaveIndicator({ status }) {
  const map = {
    saved:   { icon: '✓', text: 'Saved',   cls: 'write-save--saved' },
    saving:  { icon: '…', text: 'Saving',  cls: 'write-save--saving' },
    unsaved: { icon: '●', text: 'Unsaved', cls: 'write-save--unsaved' },
  }
  const { icon, text, cls } = map[status]
  return <span className={`write-save ${cls}`}>{icon} {text}</span>
}

function PreviewPanel({ chapter, story }) {
  return (
    <div className="write-preview">
      <div className="write-preview__inner">
        <div className="write-preview__header">
          {story.title && (
            <div
              className="write-preview__cover"
              style={{ background: COVER_GRADIENTS[story.coverIdx ?? 0].value }}
            >
              <h1 className="write-preview__story-title">{decodeEntities(story.title)}</h1>
              {story.description && (
                <p className="write-preview__story-desc">{decodeEntities(story.description)}</p>
              )}
            </div>
          )}
        </div>

        <div className="write-preview__chapter">
          <div className="write-preview__chapter-label">
            {chapter.title}
          </div>
          {chapter.content
            ? <div className="reader-content reader-content--md" dangerouslySetInnerHTML={{ __html: sanitizeStoryContent(chapter.content) }} />
            : <p className="write-preview__empty">Nothing written yet. Switch to Edit and start writing!</p>
          }
        </div>
      </div>
    </div>
  )
}

function PublishSuccess({ story, chapters, storyId, onEdit, navigate, isEdit }) {
  const totalWords = chapters.reduce((s, ch) => s + countWords(ch.content), 0)

  return (
    <div className="write-publish-success">
      <div className="write-publish-card">
        <div
          className="write-publish-card__cover"
          style={{ background: COVER_GRADIENTS[story.coverIdx ?? 0].value }}
        >
          <span className="write-publish-card__initial">
            {story.title?.[0]?.toUpperCase() || '?'}
          </span>
        </div>

        <div className="write-publish-card__body">
          <div className="write-publish-card__icon">{isEdit ? '✅' : '🎉'}</div>
          <h2 className="write-publish-card__title">{isEdit ? 'Changes saved!' : 'Your story is live!'}</h2>
          <p className="write-publish-card__desc">
            <strong>{story.title || 'Untitled'}</strong> {isEdit ? 'has been updated with' : 'has been published with'} {chapters.length}{' '}
            {chapters.length === 1 ? 'chapter' : 'chapters'} and {totalWords.toLocaleString()} words.
          </p>

          <div className="write-publish-card__tags">
            {story.genres.map((g) => (
              <span key={g} className="write-publish-card__tag">{g}</span>
            ))}
            {story.fandom && (
              <span className="write-publish-card__tag write-publish-card__tag--fandom">
                {story.fandom === 'Other' ? story.customFandom : story.fandom}
              </span>
            )}
          </div>

          <div className="write-publish-card__actions">
            <button className="btn btn-primary" onClick={() => navigate(storyId ? `/story/${storyId}` : '/')}>
              View on Verse
            </button>
            <button className="btn btn-outline" onClick={onEdit}>
              {isEdit ? 'Edit again' : 'Keep editing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
